import { Badge } from '@/components/ui/Badge';
import { Money } from '@/components/ui/Money';
import { StatTile } from '@/components/ui/StatTile';
import { cx } from '@/lib/cx';
import {
  achievedTone,
  compareDecimal,
  formatPct,
  isDecimalString,
  type SalesTargetTone,
} from '@/lib/sales-targets';
import type { SalesTargetsView } from '@/lib/types';

/**
 * Sales targets headline tiles (Management → Sales targets) and one overall
 * progress bar: achieved against target, with a marker at expected-to-date.
 *
 * Every figure is the server's USD decimal string, shown through `<Money>`;
 * nothing here sums, converts or rounds money. `null` renders `—`. The tone
 * comes from `achievedTone` in `lib/sales-targets.ts` (one copy of the rule).
 *
 * The bar's geometry is the only arithmetic: a ratio of two decimal strings,
 * taken in BigInt and reduced to basis points for a CSS width. It is never
 * shown as a figure, and an unknown or non-positive target draws nothing.
 */

const FILL: Record<SalesTargetTone, string> = {
  success: 'bg-success',
  neutral: 'bg-fg-secondary',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

/* Sized so "10,000,000.00 USD" fits a tile at 320 px (2 columns) and at lg (5). */
const VALUE = 'text-sm sm:text-base xl:text-xl';
const TILE = 'px-3 sm:px-4';

function scaled(value: string): { units: bigint; scale: number } {
  const trimmed = value.trim();
  const negative = trimmed.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? trimmed.slice(1) : trimmed).split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

/** `part / whole` in basis points (0–10000, clamped), or null when it cannot be drawn. */
function basisPoints(part: string | null, whole: string | null): number | null {
  if (!isDecimalString(part) || !isDecimalString(whole)) return null;
  const a = scaled(part);
  const b = scaled(whole);
  if (b.units <= 0n) return null;
  const scale = Math.max(a.scale, b.scale);
  const an = a.units * 10n ** BigInt(scale - a.scale);
  const bn = b.units * 10n ** BigInt(scale - b.scale);
  const bp = (an * 10000n) / bn;
  if (bp <= 0n) return 0;
  if (bp >= 10000n) return 10000;
  return Number(bp);
}

/** The magnitude of a signed decimal string (the sign is carried by the label). */
function absolute(value: string | null): string | null {
  return isDecimalString(value) && value.trim().startsWith('-') ? value.trim().slice(1) : value;
}

export function SalesTargetsSummary({ view }: { view: SalesTargetsView }) {
  const t = view.totals;
  const tone = achievedTone(t);
  const pct = isDecimalString(t.achievedPct) ? formatPct(t.achievedPct) : null;
  const exceeded = compareDecimal(t.gapUsd, '0') === -1;
  const pace = compareDecimal(t.paceGapUsd, '0');
  const behindPace = pace === 1;
  const achievedBp = basisPoints(t.achievedUsd, t.targetRevenueUsd);
  const expectedBp = basisPoints(t.expectedToDateUsd, t.targetRevenueUsd);

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        <StatTile
          className={TILE}
          label="Planned revenue"
          value={<Money amount={t.plannedRevenueUsd} currency="USD" className={VALUE} />}
        />
        <StatTile
          className={TILE}
          label="Target revenue"
          value={<Money amount={t.targetRevenueUsd} currency="USD" className={VALUE} />}
        />
        <StatTile
          className={TILE}
          label="Achieved"
          value={<Money amount={t.achievedUsd} currency="USD" className={VALUE} />}
          detail={pct === null ? '—' : `${pct} of target`}
        />
        <StatTile
          className={TILE}
          label={exceeded ? 'Ahead of target' : 'Behind target'}
          value={<Money amount={absolute(t.gapUsd)} currency="USD" className={VALUE} />}
        />
        <StatTile
          className={cx(TILE, 'col-span-2 sm:col-span-1')}
          label="Expected by today"
          value={<Money amount={t.expectedToDateUsd} currency="USD" className={VALUE} />}
          detail={
            pace === null ? (
              '—'
            ) : (
              <span className="inline-flex flex-wrap items-center gap-1.5">
                <Badge tone={behindPace ? (tone === 'danger' ? 'danger' : 'warning') : 'success'} dot>
                  {behindPace ? 'Behind pace' : 'Ahead'}
                </Badge>
                <Money amount={absolute(t.paceGapUsd)} currency="USD" />
              </span>
            )
          }
        />
      </div>

      <div
        role="progressbar"
        aria-label="Achieved against target"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={achievedBp === null ? undefined : Math.round(achievedBp / 100)}
        aria-valuetext={pct ?? '—'}
        className="relative h-2 w-full overflow-hidden rounded-full bg-surface-sunken"
      >
        {achievedBp !== null && (
          <div
            className={cx('h-full rounded-full', FILL[tone])}
            style={{ width: `${achievedBp / 100}%` }}
          />
        )}
        {expectedBp !== null && (
          <div
            aria-hidden="true"
            className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-fg"
            style={{ left: `${expectedBp / 100}%` }}
          />
        )}
      </div>
    </div>
  );
}
